

import { Component, ChangeDetectionStrategy, inject, signal, OnInit } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { DataService } from '../../services/data.service';
import { AuthService } from '../../services/auth.service';
import { LoyaltyReward, LoyaltyTier } from '../../models/index';
import { RewardFormModalComponent } from '../reward-form-modal/reward-form-modal.component';
import { TierFormModalComponent } from '../tier-form-modal/tier-form-modal.component';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';
import { NotificationService } from '../../services/notification.service';

type LoadingState = 'loading' | 'loaded' | 'error';
type ManageView = 'rewards' | 'tiers';

@Component({
  selector: 'app-promotions-management',
  standalone: true,
  templateUrl: './promotions-management.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgOptimizedImage, RewardFormModalComponent, TierFormModalComponent, TranslatePipe],
})
export class PromotionsManagementComponent implements OnInit {
  private dataService = inject(DataService);
  private translationService = inject(TranslationService);
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);

  // View state
  currentView = signal<ManageView>('rewards');
  loadingState = signal<LoadingState>('loading');
  loadingError = signal<string | null>(null);

  hasAccess = this.authService.hasFeature('loyalty');

  // Data signals
  rewards = signal<LoyaltyReward[]>([]);
  tiers = signal<LoyaltyTier[]>([]);

  // Modal states
  isRewardModalOpen = signal(false);
  editingReward = signal<LoyaltyReward | null>(null);
  isTierModalOpen = signal(false);
  editingTier = signal<LoyaltyTier | null>(null);


  constructor() {}

  ngOnInit(): void {
    if (this.hasAccess()) {
      this.loadData();
    }
  }

  async loadData(): Promise<void> {
    this.loadingState.set('loading');
    this.loadingError.set(null);
    try {
      const [rewards, tiers] = await Promise.all([
        this.dataService.getRewards(),
        this.dataService.getLoyaltyTiers(),
      ]);
      this.rewards.set(rewards.sort((a, b) => a.name.localeCompare(b.name)));
      this.tiers.set(tiers.sort((a, b) => a.min_points - b.min_points));

      this.loadingState.set('loaded');
    } catch (e) {
      this.loadingState.set('error');
      const message = (e as any).message || 'An unknown error occurred.';
      this.loadingError.set(message);
      console.error('Failed to load loyalty data:', e);
    }
  }

  switchView(view: ManageView): void {
    this.currentView.set(view);
  }

  // --- Add/Edit Modal Handlers ---
  openAddModal(): void {
    if (this.currentView() === 'rewards') {
      this.editingReward.set(null);
      this.isRewardModalOpen.set(true);
    } else if (this.currentView() === 'tiers') {
      this.editingTier.set(null);
      this.isTierModalOpen.set(true);
    }
  }

  openEditRewardModal(reward: LoyaltyReward): void {
    this.editingReward.set(reward);
    this.isRewardModalOpen.set(true);
  }

  openEditTierModal(tier: LoyaltyTier): void {
    this.editingTier.set(tier);
    this.isTierModalOpen.set(true);
  }

  // --- Save Handlers ---
  async handleSaveReward(rewardData: Omit<LoyaltyReward, 'id' | 'createdAt'> | LoyaltyReward): Promise<void> {
    try {
      if ('id' in rewardData) {
        await this.dataService.updateReward(rewardData);
      } else {
        await this.dataService.addReward(rewardData);
      }
      this.isRewardModalOpen.set(false);
      this.loadData();
    } catch (e) {
      this.notificationService.showError(`Failed to save reward: ${e instanceof Error ? e.message : 'Unknown error'}`);
    }
  }

  async handleSaveTier(tierData: Omit<LoyaltyTier, 'id' | 'createdAt'> | LoyaltyTier): Promise<void> {
    try {
      if ('id' in tierData) {
        await this.dataService.updateLoyaltyTier(tierData);
      } else {
        await this.dataService.addLoyaltyTier(tierData);
      }
      this.isTierModalOpen.set(false);
      this.loadData();
    } catch (e) {
       this.notificationService.showError(`Failed to save tier: ${e instanceof Error ? e.message : 'Unknown error'}`);
    }
  }

  // --- Delete Handlers ---
  async deleteReward(reward: LoyaltyReward): Promise<void> {
    const confirmMessage = this.translationService.translate('backOffice.loyaltyManagement.deleteConfirm', { item: reward.name });
    if (confirm(confirmMessage)) {
      await this.dataService.deleteReward(reward.id);
      this.loadData();
    }
  }

  async deleteTier(tier: LoyaltyTier): Promise<void> {
    const confirmMessage = this.translationService.translate('backOffice.loyaltyManagement.deleteConfirm', { item: tier.name });
     if (confirm(confirmMessage)) {
      await this.dataService.deleteLoyaltyTier(tier.id);
      this.loadData();
    }
  }
}
